import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEdit } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const ViewCustomer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [customerDetails, setCustomerDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        const response = await axios.get(`https://citi-mobile-app.onrender.com/saleagent/customers/${id}`);
        setCustomerDetails(response.data);
        setFormData(response.data); // Initialize form data
      } catch (error) {
        console.error('Error fetching customer details:', error);
      }
    };

    fetchCustomerDetails();
  }, [id]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.patch(`https://citi-mobile-app.onrender.com/saleagent/customers/${id}`, formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating customer details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Navigate back to the previous page
  };

  return (
    <div className="p-4 sm:p-6 max-sm:mt-20 max-sm:p-0 md:ml-0 lg:ml-60">
      <button onClick={handleBack} className="mb-4 flex items-center text-sm">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
        Back
      </button>

      <form className="bg-white shadow-md rounded px-4 sm:px-8 pt-6 pb-8 mb-4" onSubmit={handleSave}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Customer Name */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="customerName">
              Customer Name
            </label>
            <div className="flex flex-col sm:flex-row items-center">
              <input
                type="text"
                id="customerName"
                name="firstName"
                value={formData.firstName || ''}
                readOnly={!isEditing}
                onChange={handleInputChange}
                className="border border-gray-300 rounded py-2 px-4 w-full sm:w-auto"
              />
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName || ''}
                readOnly={!isEditing}
                onChange={handleInputChange}
                className="border border-gray-300 rounded py-2 px-4 w-full sm:w-auto mt-2 sm:mt-0"
              />
              <button type="button" onClick={handleEditClick} className="ml-2 mt-2 sm:mt-0">
                <FontAwesomeIcon icon={faEdit} />
              </button>
            </div>
          </div>

          {/* Phone Number */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phoneNumber">
              Phone Number
            </label>
            <input
              type="text"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* Gender */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="gender">
              Gender
            </label>
            <input
              type="text"
              id="gender"
              name="gender"
              value={formData.gender || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* Store Name */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="storeName">
              Store Name
            </label>
            <input
              type="text"
              id="storeName"
              name="storeName"
              value={formData.storeName || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* Store Type */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="storeType">
              Store Type
            </label>
            <input
              type="text"
              id="storeType"
              name="storeType"
              value={formData.storeType || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* State */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="state">
              State
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* Local Government Area */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="lga">
              Local Government Area
            </label>
            <input
              type="text"
              id="lga"
              name="lga"
              value={formData.lga || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* City */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="city">
              City
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* Locality */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="locality">
              Locality
            </label>
            <input
              type="text"
              id="locality"
              name="locality"
              value={formData.locality || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* GPS Code */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="gpsCode">
              GPS Code
            </label>
            <input
              type="text"
              id="gpsCode"
              name="gpsCode"
              value={formData.gpsCode || ''}
              readOnly={!isEditing}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* Potential Purchase */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="potentialpurchase">
              Potential Purchase
            </label>
            <input
              type="text"
              id="potentialpurchase"
              name="potentialpurchase"
              value={formData.potentialpurchase || ''}
              readOnly={true}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>

          {/* Customer Class */}
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="customerclass">
              Customer Class
            </label>
            <input
              type="text"
              id="customerclass"
              name="customerclass"
              value={formData.customerclass || ''}
              readOnly={true}
              onChange={handleInputChange}
              className="border border-gray-300 rounded py-2 px-4 w-full"
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={handleBack}
            className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 text-sm"
          >
            Cancel
          </button>
          {isEditing && (
            <button
              type="submit"
              className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 flex items-center text-sm"
              disabled={loading}
            >
              {loading ? <span className="loader mr-2"></span> : 'Save'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ViewCustomer;
